<template>
  <v-card class="align-stretch" height="100%" :loading="currentLeadLoading">
    <v-card-title>
      Sale
      <v-spacer></v-spacer>
      
      <v-btn class="mt-0" color="warning" x-small @click="voidLead(currentLead);" :disabled="!enableVoid">VOID</v-btn>
      <v-btn class="mt-0" color="error" x-small @click="lostLead(currentLead);" :disabled="currentLead.status!=='Warm'">LOST</v-btn>
      <v-btn class="mt-0" color="primary" x-small @click="sellLead();" :disabled="currentLead.status!=='Warm'">SOLD</v-btn>
      <v-btn class="mt-0" color="secondary" x-small @click="dialogOpen=true;"><v-icon small>edit</v-icon></v-btn>
    </v-card-title>

    <v-card-text v-if="!currentLeadLoading">
      <v-row>
        <v-col>

          <template v-if="!stage_edit">
            <div class="d-flex align-center">
              <v-tooltip top :disabled="currentLead.hubspot_deal_pipeline_id">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <div class="d-flex">
                      <p class="mb-1 text-caption">
                        Deal Stage
                        <v-icon small :disabled="!currentLead.hubspot_deal_pipeline_id" @click="stage_edit=true;">edit</v-icon>
                      </p>
                    </div>
                  </div>
                </template>
                <span>Cannot edit without hubspot deal pipeline id</span>
              </v-tooltip>

              <BaseInfoTooltip top>
                The stage of the account during a sale period
              </BaseInfoTooltip>
            </div>

            <p class="display-1 accent--text">{{currentLead.hubspot_deal_stage_name | notEmpty}}</p>
          </template>

          <template v-else>
            <v-row>
              <v-col cols="10"><v-autocomplete return-object v-model="deal_stage" label="Deal Stage" :items="getDealStages" :error-messages="dealStageErrors" @change="$v.deal_stage.$touch()" @blur="$v.deal_stage.$touch()" :disabled="formLoading||dealStageItemsLoading" :loading="formLoading||dealStageItemsLoading" outlined dense required></v-autocomplete></v-col>
              <v-col cols="1" class="mt-2"><v-icon color="secondary" @click="save">check</v-icon></v-col>
              <v-col cols="1" class="mt-2"><v-icon color="error" @click="stage_edit=false;">close</v-icon></v-col>
            </v-row>
          </template>

          <v-row>
            <v-col :cols="status?6:12">
              <p class="mb-1 text-caption">Status</p>
              <p><b>{{currentLead.status | notEmpty}}</b></p>
            </v-col>

            <template v-if="status">
              <v-col cols="6">
                <p class="mb-1 text-caption">{{currentLead.status}} Reason</p>
                <p><b>{{statusReason}}</b></p>
              </v-col>
            </template>
          </v-row>

          <v-row>
            <v-col>
              <p class="mb-1 text-caption">Sales Person</p>
              <p><b>{{currentLead.sales_person | notEmpty}}</b></p>
            </v-col>
            <v-col>
              <div class="d-flex align-center">
                <p class="mb-1 mr-1 text-caption">Partner / Dealer</p>
                <BaseInfoTooltip top>
                  Whether the customer is a partner
                </BaseInfoTooltip>
              </div>
              <p><b>{{partner}}</b></p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>

    <StatusDataDialog :dialogOpen="dialogOpen" @close-dialog="dialogOpen=false;" />
    <EditVoidReason :lead="voidReasonLead" :dialogOpen="voidReasonOpen" @close-dialog="voidReasonOpen=false" />
    <EditLostReason :lead="lostReasonLead" :dialogOpen="lostReasonOpen" @close-dialog="lostReasonOpen=false" />
    <SellLeadDialog :dialogOpen="sellReasonOpen" @close-dialog="sellReasonOpen=false" />
  </v-card>
</template>

<script>
  import EditVoidReason from './../Daily/EditVoidReason';
  import EditLostReason from './../Daily/EditLostReason';
  import SellLeadDialog from './../Daily/SellLeadDialog';
  import { validationMixin } from "vuelidate";
  import { required } from "vuelidate/lib/validators";
  import { mapState,mapGetters,mapActions } from "vuex";
  import StatusDataDialog from './Dialogs/StatusDataDialog';
  
  export default {
    mixins: [ validationMixin ],
    validations: {
      deal_stage: { required },
    },
    components:{StatusDataDialog,EditVoidReason,EditLostReason,SellLeadDialog},
    data() {
      return {
        deal_stage: '',
        pipeline_id: '',
        stage_edit: false,
        formLoading: false,
        dialogOpen: false,
        voidReasonOpen: false,
        voidReasonLead: '',
        lostReasonOpen: false,
        lostReasonLead: '',
        sellReasonOpen: false,
        statusReason: '',
      }
    },
    computed: {
      ...mapGetters('globals', ["hasUserGotRoles"]),
      ...mapGetters('leads',['getDealStages', 'getCurrentLeadProducts']),
      ...mapState('leads', ['currentLead','currentLeadLoading','dealStageItemsLoading']),

      dealStageErrors() {
        const errors = [];
        if (!this.$v.deal_stage.$dirty) return errors;
        !this.$v.deal_stage.required && errors.push("Deal Stage is required");
        return errors;
      },

      partner(){
        if(this.currentLead.partner_id!==null){
          if(this.currentLead.partner_object!==undefined){
            if(this.currentLead.partner_object.kind!==undefined){
              return `${this.titleCase(this.currentLead.partner_object.kind)} - ${this.currentLead.partner_object.company}`;
            }
          }
        }
        return '-';
      },

      status() {
        switch(this.currentLead.status) {
          case 'Void':
            this.statusReason = this.currentLead.void_reason;
            return true;
          case 'Lost':
            this.statusReason = this.currentLead.lost_reason;
            return true;
          default:
            return false;
        }
      },

      enableVoid() {
        if (this.currentLead.channel === 'Self Sign Up'
            && this.getCurrentLeadProducts.trial_kind === 'OPT_IN'
            && ['Trial Expired', 'Trial', 'Provisioning Failed'].includes(this.currentLead.status)
            && this.hasUserGotRoles(['can-move-voip-trial-to-void'])) {
          return true;
        }

        if (this.currentLead.status === 'Warm' || this.currentLead.status === 'Lost') {
          return true;
        }
        return  false;
      }
    },

    watch: {
      async stage_edit(val) {
        if(val){
          this.deal_stage = this.currentLead.hubspot_deal_stage_id;
          this.pipeline_id = this.currentLead.hubspot_deal_pipeline_id;
          await this.getDealStagesById(this.pipeline_id);
        }
      }
    },

    methods: {
      ...mapActions('leads',['updateDealStage','getDealStagesById']),
      ...mapActions('alerts',['setAlert']),

      titleCase(str) {
        if(str===''){ return ''; }
        return str.toLowerCase().split(' ').map(function(word) {
          return word.replace(word[0], word[0].toUpperCase());
        }).join(' ');
      },

      voidLead(item) {
        if(this.hasUserGotRoles(["can-void-leads"])) {
          this.voidReasonLead = item;
          this.voidReasonOpen = true;
        } else {
          this.setAlert({ type: 'error', message: 'You are not authorised to void leads' });
        }
      },

      lostLead(item) {
        this.lostReasonLead = item;
        this.lostReasonOpen = true;
      },

      sellLead() {
        this.sellReasonOpen = true;
      },

      async save() {
        this.$v.$touch();
        if(!this.$v.$error){
          this.formLoading = true;
          let data = {
            id: this.currentLead.id,
            data: {
              deal_stage: this.deal_stage.value,
              name: this.deal_stage.text
            }
          };
          try{
            await this.updateDealStage({sentData: data});
            this.formLoading = false;
            this.stage_edit = false;
          }catch(error){
            this.formLoading = false;
          }
        }
      },
    },

    filters: {
      notEmpty: function(val) {
        return (val === null || val === "" || val === undefined)?'-':val;
      }
    },
  }
</script>

<style lang="scss" scoped></style>
